import { useState } from "react";
import { ShieldAlert, Package, Coffee, BookOpen, Flame, Terminal, Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import CVESearch from "./CVESearch";
import FeedbackButton from "./FeedbackButton";

const SPONSOR_URL = "https://buymeacoffee.com/xbow";

const NAV = [
  { to: "/whats-broken", label: "Today", icon: Flame, testid: "nav-whats-broken" },
  { to: "/sbom", label: "SBOM scan", icon: Package, testid: "nav-sbom" },
  { to: "/developers", label: "API", icon: Terminal, testid: "nav-api" },
  { to: "/help", label: "Help", icon: BookOpen, testid: "nav-help" },
];

function NavLink({ item, active, mobile, onClick }) {
  const Icon = item.icon;
  const base = mobile
    ? "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium"
    : "hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium";
  const tone = active
    ? "bg-slate-100 text-slate-900"
    : "text-slate-600 hover:text-slate-900 hover:bg-slate-100";
  return (
    <Link
      to={item.to}
      onClick={onClick}
      data-testid={mobile ? `mobile-${item.testid}` : item.testid}
      className={`${base} ${tone} transition-colors`}
    >
      <Icon className="h-4 w-4" />
      {item.label}
    </Link>
  );
}

export default function Header({ showSearch, onSearch, loading }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  // Clicking the logo on "/" resets the dashboard (a plain Link wouldn't refire the route).
  const handleLogoClick = (e) => {
    setOpen(false);
    if (pathname === "/") {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent("app-home-reset"));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <header data-testid="header" className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 h-14 flex items-center gap-3 md:gap-5">
        <Link
          to="/"
          onClick={handleLogoClick}
          data-testid="header-logo"
          className="flex items-center gap-2 shrink-0"
        >
          <span className="h-7 w-7 rounded-md bg-slate-900 grid place-items-center">
            <ShieldAlert className="h-4 w-4 text-white" />
          </span>
          <span className="font-display font-semibold text-slate-900 hidden sm:inline">
            CVE Analytics
          </span>
        </Link>
        {showSearch ? (
          <div className="flex-1 min-w-0 max-w-xl">
            <CVESearch onSubmit={onSearch} compact loading={loading} />
          </div>
        ) : (
          <div className="flex-1" />
        )}
        <nav className="flex items-center gap-1">
          {NAV.map((item) => (
            <NavLink key={item.to} item={item} active={pathname.startsWith(item.to)} />
          ))}
        </nav>
        <div className="hidden lg:block h-5 w-px bg-slate-200" />
        <div className="hidden md:block">
          <FeedbackButton variant="header" />
        </div>
        <a
          href={SPONSOR_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="nav-support"
          className="hidden md:flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 px-2 py-1.5 rounded-md hover:bg-slate-100 transition-colors"
        >
          <Coffee className="h-4 w-4" />
          <span className="hidden xl:inline">Support</span>
        </a>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          className="lg:hidden h-9 w-9 grid place-items-center rounded-md border border-slate-200 text-slate-700 hover:bg-slate-100 transition-colors shrink-0"
        >
          {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </button>
      </div>
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-slate-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink key={item.to} item={item} mobile active={pathname.startsWith(item.to)} onClick={() => setOpen(false)} />
            ))}
            <a
              href={SPONSOR_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="mobile-nav-support"
              className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-100"
            >
              <Coffee className="h-4 w-4" />
              Support the project
            </a>
            <div className="pt-2 md:hidden">
              <FeedbackButton variant="header" />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
